import { Editor, InputRule, Node } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { EditorState, Plugin, PluginKey, TextSelection, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import {
  TaskAssignee,
  formatTaskDueDate,
  formatTaskDueDateLong,
  isEligibleTaskOwner,
  isTaskOverdue,
  isValidTaskDueDate,
  newInlineTaskId,
  taskOwnerName,
} from '../../../_util/inline-task.util';

/**
 * Inline tasks (tasks/frontend-inline-tasks-phase-1-requirements.md): a task is an `inlineTask`
 * textblock — its content is the task description, edited in place like any other line — whose
 * attributes carry the rest of the task (id, completed, owner, due date, timestamps). It is stored in
 * the description HTML as
 *
 *   <div data-type="inline-task" data-task-id="…" data-completed="false" data-owner-id="…"
 *        data-due-date="2026-10-12" data-created-at="…" data-modified-at="…">Description</div>
 *
 * so it saves, syncs and resolves conflicts with the Note. (This is unrelated to the plain
 * checklist — taskList/taskItem — which has no owner/due date and is kept as it was.)
 *
 * Shortcuts (only while the cursor is in a task, except "insert"):
 * - Mod+Alt+N: insert a task (Mod+Shift+T is the table shortcut, and a browser one)
 * - `/task ` at the start of an empty line: turn it into a task
 * - `@` (at the start or after a space): pick the owner
 * - `/due`: pick the due date; `/owner`: pick the owner
 * - Mod+Enter: toggle completed
 * - Mod+Shift+Backspace: delete the task (undo restores it)
 * - Enter: next task (on an empty task: back to a normal line); Backspace at the start: normal line
 *
 * The row's controls (checkbox, owner, due date, delete) ask the host component to open a picker by
 * dispatching INLINE_TASK_EVENT on the editor DOM; the host owns the dialogs.
 */

export const INLINE_TASK_NODE = 'inlineTask';
export const INLINE_TASK_EVENT = 'inlineTaskAction';
export const INSERT_INLINE_TASK_SHORTCUT = 'Mod-Alt-n';

export type InlineTaskAction = 'owner' | 'due' | 'deleted';
export type InlineTaskTrigger = 'click' | 'keyboard' | '@';

export interface InlineTaskEventDetail {
  action: InlineTaskAction;
  taskId: string;
  trigger: InlineTaskTrigger;
}

export interface InlineTaskAttrs {
  id: string | null;
  completed: boolean;
  ownerId: string | null;
  dueDate: string | null;
  createdAt: string | null;
  modifiedAt: string | null;
  completedAt: string | null;
}

export interface InlineTaskStorage {
  /** Eligible owners for the note (null: unknown — no owner is flagged as ineligible). */
  assignees: TaskAssignee[] | null;
  /** Set after a save was refused: empty/invalid tasks are then outlined. */
  showErrors: boolean;
  /** Row views re-render when assignees/editability change (see refreshInlineTaskViews). */
  listeners: Set<() => void>;
}

const pluginKey = new PluginKey<DecorationSet>('inlineTask');
const REFRESH_META = 'inlineTaskRefresh';

export function inlineTaskStorage(editor: Editor): InlineTaskStorage {
  return (editor.storage as unknown as Record<string, InlineTaskStorage>)[INLINE_TASK_NODE];
}

export function newInlineTaskAttrs(now: string = new Date().toISOString()): InlineTaskAttrs {
  return { id: newInlineTaskId(), completed: false, ownerId: null, dueDate: null, createdAt: now, modifiedAt: now, completedAt: null };
}

// --- Finding tasks ---

export interface FoundInlineTask {
  node: ProseMirrorNode;
  pos: number;
}

export function findInlineTask(doc: ProseMirrorNode, taskId: string): FoundInlineTask | null {
  let found: FoundInlineTask | null = null;
  doc.descendants((node, pos) => {
    if (found) return false;
    if (node.type.name === INLINE_TASK_NODE) {
      if (node.attrs['id'] === taskId) found = { node, pos };
      return false;
    }
    return true;
  });
  return found;
}

export function allInlineTasks(doc: ProseMirrorNode): FoundInlineTask[] {
  const tasks: FoundInlineTask[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name === INLINE_TASK_NODE) {
      tasks.push({ node, pos });
      return false;
    }
    return true;
  });
  return tasks;
}

/** The task holding the cursor (selection entirely inside one task). */
export function focusedInlineTask(state: EditorState): FoundInlineTask | null {
  const { $from, $to } = state.selection;
  if (!$from.sameParent($to) || $from.parent.type.name !== INLINE_TASK_NODE) return null;
  return { node: $from.parent, pos: $from.before() };
}

export function hasInlineTasks(doc: ProseMirrorNode): boolean {
  return allInlineTasks(doc).length > 0;
}

// --- Commands (plain functions over an Editor; all go through transactions, so undo works) ---

function dispatchTaskEvent(editor: Editor, detail: InlineTaskEventDetail): void {
  editor.view.dom.dispatchEvent(new CustomEvent<InlineTaskEventDetail>(INLINE_TASK_EVENT, { detail, bubbles: true }));
}

/** Insert a task: the current empty/plain line becomes one, otherwise a new task goes after the
 * current block. The cursor ends up in the task's description. */
export function insertInlineTask(editor: Editor): boolean {
  if (!editor.isEditable) return false;
  const { state } = editor;
  const type = state.schema.nodes[INLINE_TASK_NODE];
  const { $from } = state.selection;
  const attrs = newInlineTaskAttrs();

  // Already in a task: a new one right after it.
  const inTask = focusedInlineTask(state);
  if (inTask) {
    const after = inTask.pos + inTask.node.nodeSize;
    const tr = state.tr.insert(after, type.create(attrs));
    tr.setSelection(TextSelection.create(tr.doc, after + 1)).scrollIntoView();
    editor.view.dispatch(tr);
    editor.view.focus();
    return true;
  }

  // A plain paragraph that may become a task (not e.g. the first line of a list item).
  if ($from.parent.type.name === 'paragraph' && $from.sameParent(state.selection.$to) && $from.depth >= 1) {
    const index = $from.index(-1);
    if ($from.node(-1).canReplaceWith(index, index + 1, type)) {
      const tr = state.tr.setBlockType($from.before(), $from.after(), type, attrs).scrollIntoView();
      editor.view.dispatch(tr);
      editor.view.focus();
      return true;
    }
  }

  // Elsewhere (a heading, a list item, a table cell…): after the innermost block whose container
  // takes a task there.
  let after = $from.pos;
  for (let depth = $from.depth; depth >= 1; depth--) {
    const container = $from.node(depth - 1);
    const index = $from.indexAfter(depth - 1);
    if (container.canReplaceWith(index, index, type)) {
      after = $from.after(depth);
      break;
    }
  }
  const tr = state.tr.insert(after, type.create(attrs));
  tr.setSelection(TextSelection.create(tr.doc, after + 1)).scrollIntoView();
  editor.view.dispatch(tr);
  editor.view.focus();
  return true;
}

export function updateInlineTask(editor: Editor, taskId: string, changes: Partial<InlineTaskAttrs>): boolean {
  if (!editor.isEditable) return false;
  const found = findInlineTask(editor.state.doc, taskId);
  if (!found) return false;
  const tr = editor.state.tr.setNodeMarkup(found.pos, undefined, { ...found.node.attrs, ...changes });
  editor.view.dispatch(tr);
  return true;
}

export function toggleInlineTask(editor: Editor, taskId: string): boolean {
  const found = findInlineTask(editor.state.doc, taskId);
  if (!found) return false;
  const completed = !found.node.attrs['completed'];
  return updateInlineTask(editor, taskId, { completed, completedAt: completed ? new Date().toISOString() : null });
}

/** Remove the task; the cursor moves to where it was. A task that is its container's only block is
 * replaced by an empty line, so the document stays valid. */
export function deleteInlineTask(editor: Editor, taskId: string): boolean {
  if (!editor.isEditable) return false;
  const found = findInlineTask(editor.state.doc, taskId);
  if (!found) return false;
  const { state } = editor;
  const $pos = state.doc.resolve(found.pos);
  const end = found.pos + found.node.nodeSize;
  const tr =
    $pos.parent.childCount === 1
      ? state.tr.replaceWith(found.pos, end, state.schema.nodes['paragraph'].create())
      : state.tr.delete(found.pos, end);
  const target = Math.min(found.pos, tr.doc.content.size);
  tr.setSelection(TextSelection.near(tr.doc.resolve(target), target > 0 ? -1 : 1)).scrollIntoView();
  editor.view.dispatch(tr);
  return true;
}

/** Turn the task back into a normal line, keeping its text. */
function convertTaskToParagraph(state: EditorState, dispatch: ((tr: Transaction) => void) | undefined, pos: number, node: ProseMirrorNode): boolean {
  const paragraph = state.schema.nodes['paragraph'];
  if (dispatch) dispatch(state.tr.setBlockType(pos, pos + node.nodeSize, paragraph).scrollIntoView());
  return true;
}

/** Re-render every task row (owner names, eligibility, editability) after a host-side change. */
export function refreshInlineTaskViews(editor: Editor): void {
  const storage = inlineTaskStorage(editor);
  storage?.listeners.forEach((listener) => listener());
  if (!editor.isDestroyed) editor.view.dispatch(editor.state.tr.setMeta(REFRESH_META, true).setMeta('addToHistory', false));
}

/** Outline empty/invalid tasks (after a refused save). */
export function revealInlineTaskErrors(editor: Editor): void {
  const storage = inlineTaskStorage(editor);
  if (storage) storage.showErrors = true;
  refreshInlineTaskViews(editor);
}

// --- Bookkeeping: ids unique, modifiedAt/completedAt kept current ---

function taskSignature(node: ProseMirrorNode): string {
  const a = node.attrs;
  return JSON.stringify([node.textContent, !!a['completed'], a['ownerId'] ?? null, a['dueDate'] ?? null]);
}

/** Appended after user edits (never after setContent from outside — that is the saved state). */
export function inlineTaskBookkeeping(transactions: readonly Transaction[], oldState: EditorState, newState: EditorState): Transaction | null {
  if (!transactions.some((tr) => tr.docChanged)) return null;
  if (transactions.some((tr) => tr.getMeta('preventUpdate'))) return null;

  const before = new Map<string, string>();
  for (const { node } of allInlineTasks(oldState.doc)) {
    if (node.attrs['id']) before.set(node.attrs['id'], taskSignature(node));
  }

  const now = new Date().toISOString();
  const seen = new Set<string>();
  const tr = newState.tr;
  for (const { node, pos } of allInlineTasks(newState.doc)) {
    const attrs = node.attrs as InlineTaskAttrs;
    let next: InlineTaskAttrs | null = null;
    if (!attrs.id || seen.has(attrs.id)) {
      // New, or a copy (pasted/duplicated) of a task that's already in the note.
      next = { ...attrs, id: newInlineTaskId(), createdAt: now, modifiedAt: now };
    } else {
      const previous = before.get(attrs.id);
      if (previous !== undefined && previous !== taskSignature(node)) next = { ...attrs, modifiedAt: now };
    }
    const current = next ?? attrs;
    if (current.completed && !current.completedAt) next = { ...current, completedAt: now };
    else if (!current.completed && current.completedAt) next = { ...current, completedAt: null };
    seen.add((next ?? attrs).id!);
    if (next) tr.setNodeMarkup(pos, undefined, next);
  }
  return tr.docChanged ? tr : null;
}

// --- Row view ---

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, attrs: Record<string, string> = {}): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  element.className = className;
  for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, value);
  return element;
}

/** The button is the (touch-sized) hit area; the pill inside is what's seen. */
function setIconText(button: HTMLElement, icon: string, text: string): void {
  const pill = el('span', 'inline-task__pill');
  pill.appendChild(el('span', `pi ${icon} inline-task__chip-icon`, { 'aria-hidden': 'true' }));
  if (text) {
    const label = el('span', 'inline-task__chip-text');
    label.textContent = text;
    pill.appendChild(label);
  }
  button.replaceChildren(pill);
}

export const InlineTask = Node.create<Record<string, never>, InlineTaskStorage>({
  name: INLINE_TASK_NODE,
  group: 'block',
  content: 'inline*',
  defining: true,
  // Before the list/collapsible keymaps: Enter/Backspace/Mod-Enter mean something else in a task.
  priority: 1000,

  addStorage() {
    return { assignees: null, showErrors: false, listeners: new Set<() => void>() };
  },

  addAttributes() {
    const text = (name: string) => ({
      default: null,
      parseHTML: (element: HTMLElement) => element.getAttribute(`data-${name}`) || null,
      renderHTML: (attributes: Record<string, unknown>) => {
        const key = name.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
        return attributes[key] ? { [`data-${name}`]: attributes[key] } : {};
      },
    });
    return {
      id: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-task-id') || newInlineTaskId(),
        renderHTML: (attributes: Record<string, unknown>) => ({ 'data-task-id': attributes['id'] }),
      },
      completed: {
        default: false,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-completed') === 'true',
        renderHTML: (attributes: Record<string, unknown>) => ({ 'data-completed': attributes['completed'] ? 'true' : 'false' }),
      },
      ownerId: text('owner-id'),
      dueDate: text('due-date'),
      createdAt: text('created-at'),
      modifiedAt: text('modified-at'),
      completedAt: text('completed-at'),
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="inline-task"]', priority: 60 }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', { ...HTMLAttributes, 'data-type': 'inline-task', class: 'inline-task' }, 0];
  },

  addNodeView() {
    return ({ node: initialNode, editor, getPos }) => {
      let node = initialNode;
      const storage = inlineTaskStorage(editor);
      const taskId = () => String(node.attrs['id'] ?? '');

      const dom = el('div', 'inline-task', { 'data-type': 'inline-task' });
      const check = el('label', 'inline-task__check', { contenteditable: 'false' });
      const checkbox = el('input', 'inline-task__checkbox', { type: 'checkbox' }) as HTMLInputElement;
      check.appendChild(checkbox);
      const text = el('div', 'inline-task__text', { 'data-placeholder': 'Task description' });
      const meta = el('span', 'inline-task__meta', { contenteditable: 'false' });
      const owner = el('button', 'inline-task__chip inline-task__owner', { type: 'button', 'data-testid': 'inline-task-owner' });
      const due = el('button', 'inline-task__chip inline-task__due', { type: 'button', 'data-testid': 'inline-task-due' });
      const remove = el('button', 'inline-task__chip inline-task__delete', { type: 'button', 'data-testid': 'inline-task-delete', 'aria-label': 'Delete task', title: 'Delete task (Ctrl/⌘+Shift+Backspace)' });
      setIconText(remove, 'pi-trash', '');
      meta.append(owner, due, remove);
      dom.append(check, text, meta);

      const render = () => {
        const editable = editor.isEditable;
        const attrs = node.attrs as InlineTaskAttrs;
        const description = node.textContent.trim() || 'untitled task';
        const assignees = storage?.assignees ?? null;

        dom.dataset['taskId'] = taskId();
        dom.classList.toggle('inline-task--completed', !!attrs.completed);
        dom.classList.toggle('inline-task--readonly', !editable);
        dom.setAttribute('data-completed', attrs.completed ? 'true' : 'false');

        checkbox.checked = !!attrs.completed;
        checkbox.disabled = !editable;
        checkbox.setAttribute('aria-label', `${attrs.completed ? 'Completed' : 'Open'} task: ${description}. ${attrs.completed ? 'Mark as not done' : 'Mark as done'} (Ctrl/⌘+Enter)`);

        // Owner
        const ownerName = taskOwnerName(attrs.ownerId, assignees);
        const ownerEligible = isEligibleTaskOwner(attrs.ownerId, assignees);
        owner.hidden = !editable && !attrs.ownerId;
        owner.disabled = !editable;
        owner.classList.toggle('inline-task__chip--empty', !attrs.ownerId);
        owner.classList.toggle('inline-task__chip--error', !ownerEligible);
        if (!attrs.ownerId) {
          setIconText(owner, 'pi-user-plus', '');
          owner.setAttribute('aria-label', 'Assign owner (@)');
          owner.title = 'Assign owner (type @ in the task)';
        } else if (!ownerEligible) {
          setIconText(owner, 'pi-exclamation-triangle', ownerName);
          owner.setAttribute('aria-label', `Owner ${ownerName} no longer has access to this note.${editable ? ' Change owner' : ''}`);
          owner.title = `${ownerName} no longer has access to this note`;
        } else {
          setIconText(owner, 'pi-user', ownerName);
          owner.setAttribute('aria-label', `Owner: ${ownerName}.${editable ? ' Change owner (@)' : ''}`);
          owner.title = editable ? 'Change owner (type @ in the task)' : `Owner: ${ownerName}`;
        }

        // Due date
        const dueValid = !attrs.dueDate || isValidTaskDueDate(attrs.dueDate);
        const overdue = isTaskOverdue({ dueDate: attrs.dueDate, completed: attrs.completed });
        due.hidden = !editable && !attrs.dueDate;
        due.disabled = !editable;
        due.classList.toggle('inline-task__chip--empty', !attrs.dueDate);
        due.classList.toggle('inline-task__chip--error', !dueValid);
        due.classList.toggle('inline-task__chip--overdue', overdue);
        dom.classList.toggle('inline-task--overdue', overdue);
        if (!attrs.dueDate) {
          setIconText(due, 'pi-calendar-plus', '');
          due.setAttribute('aria-label', 'Set due date (/due)');
          due.title = 'Set due date (type /due in the task)';
        } else if (!dueValid) {
          setIconText(due, 'pi-exclamation-triangle', 'Invalid date');
          due.setAttribute('aria-label', `Invalid due date.${editable ? ' Change due date' : ''}`);
          due.title = 'Invalid due date';
        } else {
          setIconText(due, overdue ? 'pi-exclamation-circle' : 'pi-calendar', formatTaskDueDate(attrs.dueDate));
          const long = formatTaskDueDateLong(attrs.dueDate);
          due.setAttribute('aria-label', `${overdue ? 'Overdue, was due' : 'Due'} ${long}.${editable ? ' Change due date (/due)' : ''}`);
          due.title = overdue ? `Overdue — was due ${long}` : `Due ${long}`;
        }

        remove.hidden = !editable;
      };

      const fire = (action: InlineTaskAction) => (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!editor.isEditable) return;
        if (action === 'deleted') {
          const id = taskId();
          if (deleteInlineTask(editor, id)) dispatchTaskEvent(editor, { action, taskId: id, trigger: 'click' });
          return;
        }
        dispatchTaskEvent(editor, { action, taskId: taskId(), trigger: 'click' });
      };
      owner.addEventListener('click', fire('owner'));
      due.addEventListener('click', fire('due'));
      remove.addEventListener('click', fire('deleted'));
      checkbox.addEventListener('change', () => {
        if (!editor.isEditable) {
          checkbox.checked = !!node.attrs['completed'];
          return;
        }
        if (typeof getPos === 'function' && getPos() !== undefined) toggleInlineTask(editor, taskId());
      });

      storage?.listeners.add(render);
      render();

      return {
        dom,
        contentDOM: text,
        update: (updated) => {
          if (updated.type !== node.type) return false;
          node = updated;
          render();
          return true;
        },
        // Clicks/keys on the controls are theirs, not the editor's (Tab moves between them normally).
        stopEvent: (event) => !!(event.target instanceof globalThis.Node && (check.contains(event.target) || meta.contains(event.target))),
        ignoreMutation: (mutation) => mutation.type !== 'selection' && !text.contains(mutation.target),
        destroy: () => {
          storage?.listeners.delete(render);
        },
      };
    };
  },

  addKeyboardShortcuts() {
    return {
      [INSERT_INLINE_TASK_SHORTCUT]: ({ editor }) => insertInlineTask(editor),

      Enter: ({ editor }) => {
        const found = focusedInlineTask(editor.state);
        if (!found) return false;
        const { state } = editor;
        const type = state.schema.nodes[INLINE_TASK_NODE];
        // Empty task: back to a normal line (like Enter on an empty list item).
        if (found.node.content.size === 0) {
          return convertTaskToParagraph(state, editor.view.dispatch, found.pos, found.node);
        }
        let tr = state.tr;
        if (!state.selection.empty) tr = tr.deleteSelection();
        const $pos = tr.selection.$from;
        if ($pos.parentOffset === 0) {
          // At the start: a new empty task above, this one (and its id) moves down.
          tr.insert($pos.before(), type.create(newInlineTaskAttrs()));
        } else {
          tr.split($pos.pos, 1, [{ type, attrs: newInlineTaskAttrs() }]);
        }
        editor.view.dispatch(tr.scrollIntoView());
        return true;
      },

      Backspace: ({ editor }) => {
        const { selection } = editor.state;
        const found = focusedInlineTask(editor.state);
        if (!found || !selection.empty || selection.$from.parentOffset !== 0) return false;
        return convertTaskToParagraph(editor.state, editor.view.dispatch, found.pos, found.node);
      },

      'Mod-Enter': ({ editor }) => {
        const found = focusedInlineTask(editor.state);
        return !!found && toggleInlineTask(editor, found.node.attrs['id']);
      },

      'Mod-Shift-Backspace': ({ editor }) => {
        const found = focusedInlineTask(editor.state);
        if (!found) return false;
        const id = found.node.attrs['id'];
        if (deleteInlineTask(editor, id)) dispatchTaskEvent(editor, { action: 'deleted', taskId: id, trigger: 'keyboard' });
        return true;
      },
    };
  },

  addInputRules() {
    const type = this.type;
    const editor = this.editor;
    // `/due` / `/owner` typed in a task (at its start or after a space) open the pickers.
    const pickerRule = (find: RegExp, action: InlineTaskAction) =>
      new InputRule({
        find,
        handler: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          if ($from.parent.type !== type) return null;
          const taskId = $from.parent.attrs['id'];
          state.tr.delete(range.from, range.to);
          setTimeout(() => dispatchTaskEvent(editor, { action, taskId, trigger: 'keyboard' }));
          return undefined;
        },
      });
    return [
      // `/task ` at the start of an empty line: make it a task. (`[ ] ` is the plain checklist.)
      new InputRule({
        find: /^\/task\s$/,
        handler: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          if ($from.parent.type.name !== 'paragraph' || $from.depth < 1) return null;
          const index = $from.index(-1);
          if (!$from.node(-1).canReplaceWith(index, index + 1, type)) return null;
          state.tr.delete(range.from, range.to).setBlockType(range.from, range.from, type, newInlineTaskAttrs());
          return undefined;
        },
      }),
      pickerRule(/(?:^|(?<=\s))\/due$/, 'due'),
      pickerRule(/(?:^|(?<=\s))\/owner$/, 'owner'),
    ];
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const storage = this.storage;
    const decorate = (state: EditorState): DecorationSet => {
      const focused = focusedInlineTask(state);
      const decorations: Decoration[] = [];
      for (const { node, pos } of allInlineTasks(state.doc)) {
        const classes: string[] = [];
        const empty = node.textContent.trim() === '';
        if (empty) classes.push('inline-task--empty');
        if (focused?.pos === pos && editor.isEditable) classes.push('inline-task--focused');
        if (
          storage.showErrors &&
          (empty ||
            (node.attrs['dueDate'] && !isValidTaskDueDate(node.attrs['dueDate'])) ||
            !isEligibleTaskOwner(node.attrs['ownerId'], storage.assignees))
        ) {
          classes.push('inline-task--invalid');
        }
        if (classes.length) {
          decorations.push(
            Decoration.node(pos, pos + node.nodeSize, {
              class: classes.join(' '),
              ...(classes.includes('inline-task--invalid') ? { 'aria-invalid': 'true' } : {}),
            }),
          );
        }
      }
      return DecorationSet.create(state.doc, decorations);
    };

    return [
      new Plugin({
        key: pluginKey,
        state: {
          init: (_, state) => decorate(state),
          apply: (tr, value, _old, newState) =>
            tr.docChanged || tr.selectionSet || tr.getMeta(REFRESH_META) ? decorate(newState) : value,
        },
        props: {
          decorations: (state) => pluginKey.getState(state),
          // `@` in a task (at its start or after a space) opens the owner picker instead of typing it;
          // the host types the `@` after all if the picker is dismissed (an email address still works).
          handleTextInput: (view, from, to, text) => {
            if (text !== '@' || !editor.isEditable) return false;
            const $from = view.state.doc.resolve(from);
            if ($from.parent.type.name !== INLINE_TASK_NODE || from !== to) return false;
            const before = $from.parent.textBetween(0, $from.parentOffset);
            if (before !== '' && !/\s$/.test(before)) return false;
            dispatchTaskEvent(editor, { action: 'owner', taskId: $from.parent.attrs['id'], trigger: '@' });
            return true;
          },
        },
        appendTransaction: (transactions, oldState, newState) => inlineTaskBookkeeping(transactions, oldState, newState),
      }),
    ];
  },
});
